import { MdsLink, MDSSection, MDSSectionSlotName } from "@/mds";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { windowModule } from "@/modules/window";
import {
  StyledContentList,
  StyledContentSectionTitleWrapper,
  StyledContentStatusWrapper,
} from "@/pages/integrations/shared/integrations.styles";
import { css } from "@/domains/emotion";
import { APP_CONFIG } from "@/domains/global/config";

interface IntegrationsZapierPageDescriptionProps {
  isConnected: boolean;
  onZapierIntegrationLinkClick: () => void;
}

export const IntegrationsZapierPageDescription: FC<IntegrationsZapierPageDescriptionProps> =
  observer(({ isConnected, onZapierIntegrationLinkClick }) => {
    return (
      <MDSSection
        title={
          <StyledContentSectionTitleWrapper>
            <MdsIcon kind={MdsIconKind.LogoZapier} />
            Zapier Integration
          </StyledContentSectionTitleWrapper>
        }
      >
        <div slot={MDSSectionSlotName.HeaderRightAddon}>
          {isConnected && (
            <StyledContentStatusWrapper>
              Connected
              <MdsIcon kind={MdsIconKind.CheckCircleSolid} />
            </StyledContentStatusWrapper>
          )}
        </div>
        <StyledContent>
          <StyledIntroText>
            <WebsiteLink onClick={onZapierIntegrationLinkClick}>Zapier</WebsiteLink> lets you
            connect Mem to 8,000+ apps without writing a single line of code.
          </StyledIntroText>
          <StyledSecondaryText>A few popular use-cases:</StyledSecondaryText>
          <StyledIconList>
            <li>
              <StyledListBoldText>
                <InlineMdsIcon
                  kind={MdsIconKind.Microphone}
                  innerStyles={{ Icon: { className: inlineChipIconStyles } }}
                />{" "}
                Meeting Transcription Archive
              </StyledListBoldText>{" "}
              - Automatically import and format meeting transcripts from tools like Fireflies,
              Otter, and Fathom.
            </li>
            <li>
              <StyledListBoldText>
                <InlineMdsIcon
                  kind={MdsIconKind.Comment}
                  innerStyles={{ Icon: { className: inlineChipIconStyles } }}
                />{" "}
                Slack Message Capture
              </StyledListBoldText>{" "}
              - Save important messages and conversations from Slack to Mem with a simple star or
              reaction.
            </li>
            <li>
              <StyledListBoldText>
                <InlineMdsIcon
                  kind={MdsIconKind.Folder}
                  innerStyles={{ Icon: { className: inlineChipIconStyles } }}
                />{" "}
                Google Drive Sync
              </StyledListBoldText>{" "}
              - Bring documents from specific folders in Google Drive into Mem, ensuring your
              important work is always searchable alongside your notes.
            </li>
            <li>
              <StyledListBoldText>
                <InlineMdsIcon
                  kind={MdsIconKind.ListCheck}
                  innerStyles={{ Icon: { className: inlineChipIconStyles } }}
                />{" "}
                Task Completion Journal
              </StyledListBoldText>{" "}
              - Track completed tasks from Todoist, Asana, or other task managers to maintain a
              searchable work log.
            </li>
            <li>
              <StyledListBoldText>
                <InlineMdsIcon
                  kind={MdsIconKind.Clipboard}
                  innerStyles={{ Icon: { className: inlineChipIconStyles } }}
                />{" "}
                Form Response Collector
              </StyledListBoldText>{" "}
              - Capture submissions from Google Forms, Typeform, or other survey tools directly in
              Mem for organized feedback management.
            </li>
          </StyledIconList>
          <StyledVideoSection>
            <StyledVideoText>
              Automate Your Meeting Notes in Mem -{" "}
              <WebsiteLink
                onClick={() =>
                  windowModule.openInNewTab({
                    url: new URL(APP_CONFIG.URLS.ZAPIER_VIDEO_URL),
                  })
                }
              >
                Watch Video
              </WebsiteLink>
            </StyledVideoText>
            <ZapierIframe
              src={APP_CONFIG.URLS.ZAPIER_VIDEO_EMBED_URL}
              title="Automate Your Meeting Notes with AI in Mem"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </StyledVideoSection>
        </StyledContent>
      </MDSSection>
    );
  });

const StyledListBoldText = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const StyledContent = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ theme }) => theme.colors.grey.x600};
`;

const WebsiteLink = styled(MdsLink)(({ theme }) => ({
  fontWeight: theme.fontWeights.medium,
}));

const StyledIntroText = styled.div`
  line-height: ${({ theme }) => theme.lineHeights.xxlarge};
`;

const StyledSecondaryText = styled.div`
  padding-top: 14px;
  line-height: ${({ theme }) => theme.lineHeights.xlarge};
`;

const InlineMdsIcon = styled(MdsIcon)`
  display: inline-block;
  margin-right: 1px;
  text-align: center;
  width: 20px;
`;

const StyledIconList = styled(StyledContentList)`
  list-style-type: none;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const inlineChipIconStyles = css`
  font-size: 18px;
`;

const StyledVideoSection = styled.div`
  padding-top: 6px;
`;

const StyledVideoText = styled.div`
  padding-top: 14px;
  line-height: ${({ theme }) => theme.lineHeights.xlarge};
`;

const ZapierIframe = styled.iframe`
  border: none;
  padding-top: 8px;
  padding-right: 16px;
  width: 50vw;
  max-width: 600px;
  min-width: 300px;
  height: 25vw;
  max-height: 300px;
  min-height: 150px;
`;
